import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/Authcontext';

const LEVEL_STYLES = [
  { level: 1, label: 'Direct Referral', color: 'bg-amber-500'   },
  { level: 2, label: '2nd Level',       color: 'bg-orange-400'  },
  { level: 3, label: '3rd Level',       color: 'bg-yellow-500'  },
  { level: 4, label: '4th Level',       color: 'bg-lime-500'    },
  { level: 5, label: '5th Level',       color: 'bg-emerald-500' },
  { level: 6, label: '6th Level',       color: 'bg-teal-500'    },
  { level: 7, label: '7th Level',       color: 'bg-blue-500'    },
];

const PAYOUT_PLANS = [
  {
    id: 'month1',
    title: '1st Month Income Payout',
    subtitle: 'One-time bonus when a new member joins and pays',
    pts: [200, 50, 10, 10, 10, 10, 10],
    perLabel: 'one-time',
    tabActiveClass: 'bg-violet-600 text-white border-violet-600',
    footerClass: 'bg-violet-50 border-violet-100 text-violet-800',
  },
  {
    id: 'month2_9',
    title: 'Month 2–9 Income Payout',
    subtitle: 'Recurring income each month from month 2 through month 9',
    pts: [30, 10, 5, 3, 2, 1, 0.5],
    perLabel: 'per month',
    tabActiveClass: 'bg-cyan-600 text-white border-cyan-600',
    footerClass: 'bg-cyan-50 border-cyan-100 text-cyan-800',
  },
];

const formatPts = (pts) => (Number.isInteger(pts) ? String(pts) : pts.toFixed(1));

function levelsForPlan(plan) {
  const maxPts = Math.max(...plan.pts);
  return LEVEL_STYLES.map((style, index) => ({
    ...style,
    pts: plan.pts[index],
    barWidth: `${Math.max(8, (plan.pts[index] / maxPts) * 100)}%`,
  }));
}

const STEPS = [
  { n: '01', title: 'Pay ₹500/month',        sub: 'Activate your membership to access the full platform and referral earnings.',                         icon: '💳' },
  { n: '02', title: 'Get your referral link',   sub: 'Share your unique link with anyone. When they join and pay, you earn points automatically.',          icon: '🔗' },
  { n: '03', title: 'Earn from 7 levels',       sub: 'You earn when anyone in your 7-level deep network joins or renews their monthly membership.',        icon: '📈' },
  { n: '04', title: 'Withdraw your earnings',   sub: '1 point = ₹1. Withdraw to your bank or UPI when you hit ₹500 minimum.',                             icon: '🏦' },
];

const RANK_DATA = [
  { rank: 'Bronze',           ids: '50+',    color: 'text-orange-600',  icon: '🥉' },
  { rank: 'Silver',           ids: '100+',   color: 'text-slate-500',   icon: '🥈' },
  { rank: 'Gold',             ids: '250+',   color: 'text-amber-600',   icon: '🥇' },
  { rank: 'Diamond',          ids: '500+',   color: 'text-cyan-600',    icon: '💎' },
  { rank: 'Black Diamond',    ids: '1,000+', color: 'text-violet-700',  icon: '◆' },
  { rank: 'Advisory Member',  ids: '1,500+', color: 'text-indigo-700',  icon: '👑' },
];

function Home() {
  const { user } = useAuth();
  const [payoutPlanId, setPayoutPlanId] = useState('month1');
  const activePlan = PAYOUT_PLANS.find((p) => p.id === payoutPlanId) || PAYOUT_PLANS[0];
  const levelRows = levelsForPlan(activePlan);
  const planTotal = activePlan.pts.reduce((sum, pts) => sum + pts, 0);

  return (
    <div className="min-h-screen bg-slate-50">

      {/* ── HERO ── */}
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0 opacity-5"
          style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="relative max-w-5xl mx-auto px-6 py-20 sm:py-28 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-xs font-bold text-slate-300 mb-6 uppercase tracking-widest">
            🌐 7-Level Community Platform
          </div>
          <h1 className="text-4xl sm:text-6xl font-black text-white leading-tight mb-5 tracking-tight"
            style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
            Build Your Network.<br />
            <span className="text-amber-400">Earn Every Month.</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-xl mx-auto mb-8 leading-relaxed">
            Join SuccessFlow for ₹500/month. Refer friends, earn points when anyone in your 7-level network pays their membership — every single month.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {user ? (
              <Link to="/dashboard"
                className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-black text-base px-8 py-4 rounded-2xl no-underline transition-all active:scale-95 shadow-xl">
                Go to Dashboard →
              </Link>
            ) : (
              <>
                {/* <Link to="/login?mode=signup"
                  className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-black text-base px-8 py-4 rounded-2xl no-underline transition-all active:scale-95 shadow-xl">
                  Join Now — ₹500/mo →
                </Link> */}
                <Link to="/login"
                  className="border border-white/30 text-white hover:bg-white/10 font-semibold text-sm px-6 py-4 rounded-2xl no-underline transition-all">
                  Sign In
                </Link>
                <Link to="/plans"
                className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-black text-base px-8 py-4 rounded-2xl no-underline transition-all active:scale-95 shadow-xl">
                Go to Plans →
              </Link>

              </>
            )}
          </div>
          {/* Stats row */}
          <div className="flex flex-wrap items-center justify-center gap-8 mt-14 pt-10 border-t border-white/10">
            {[
              { val: '5,240',   label: 'Active Members'  },
              { val: '₹500',  label: 'Per Month'       },
              { val: '7',       label: 'Earning Levels'  },
              { val: '₹300',    label: 'Max Month 1 Chain'},
            ].map((s, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl font-black text-white" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>{s.val}</p>
                <p className="text-xs text-slate-400 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Simple Process</p>
            <h2 className="text-3xl font-black text-slate-900" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
              How SuccessFlow Works
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-5 relative">
                <span className="text-[10px] font-black text-slate-300 absolute top-4 right-4">{s.n}</span>
                <div className="text-3xl mb-4">{s.icon}</div>
                <h3 className="text-sm font-black text-slate-900 mb-2">{s.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── EARNING STRUCTURE ── */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Commission Structure</p>
            <h2 className="text-3xl font-black text-slate-900 mb-3" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
              Earn from 7 Levels Deep
            </h2>
            <p className="text-sm text-slate-500 max-w-lg mx-auto">
              Two payout phases: a strong 1st-month bonus when someone joins, then recurring income from month 2 through month 9.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
            {PAYOUT_PLANS.map((plan) => {
              const isActive = payoutPlanId === plan.id;
              return (
              <button
                key={plan.id}
                type="button"
                onClick={() => setPayoutPlanId(plan.id)}
                className={`rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
                  isActive
                    ? plan.tabActiveClass
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {plan.id === 'month1' ? 'Month 1 Payout' : 'Month 2–9 Payout'}
              </button>
              );
            })}
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
              <p className="text-sm font-black text-slate-900">{activePlan.title}</p>
              <p className="text-xs text-slate-500 mt-1">{activePlan.subtitle}</p>
            </div>
            <div className="p-6 space-y-4">
              {levelRows.map((l) => (
                <div key={l.level} className="flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-xl ${l.color} flex items-center justify-center text-white text-sm font-black flex-shrink-0`}>
                    {l.level}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-slate-700">{l.label}</span>
                      <span className="text-sm font-black text-slate-900">
                        +{formatPts(l.pts)} pts{' '}
                        <span className="text-xs text-slate-400">(₹{formatPts(l.pts)})</span>
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${l.color} rounded-full`}
                        style={{ width: l.barWidth }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className={`px-6 py-4 border-t ${activePlan.footerClass}`}>
              <p className="text-xs">
                <strong>Max per full chain: {formatPts(planTotal)} points (₹{formatPts(planTotal)})</strong>
                {' · '}
                {activePlan.id === 'month2_9'
                  ? 'Recurring every month from month 2 through month 9'
                  : 'One-time payout when a new member joins and pays'}
                {' · '}
                1 point = ₹1 withdrawal value
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── RANK SYSTEM ── */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Rank System</p>
            <h2 className="text-3xl font-black text-slate-900" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
              Rise Through the Ranks
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {RANK_DATA.map((r, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-center">
                <div className="text-3xl mb-2">{r.icon}</div>
                <p className={`text-sm font-black ${r.color} mb-1 leading-snug`}>{r.rank}</p>
                <p className="text-xs text-slate-400">{r.ids} IDs</p>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-slate-400 mt-5">
            Ranks are based on activated members in your network (all levels), not reward points. Higher ranks unlock exclusive benefits and recognition.
          </p>
        </div>
      </section>

      {/* ── EXAMPLE EARNINGS ── */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs font-bold text-amber-400 uppercase tracking-widest mb-3">Example Scenario</p>
            <h2 className="text-3xl font-black text-white" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
              What Could You Earn?
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
            {[
              { title: 'Starter',    refs: 5,  net: 4,   l2: 10,  l3: 20, monthly: '₹1,400', color: 'border-slate-600' },
              { title: 'Active',     refs: 10, net: 30,  l2: 50,  l3: 80, monthly: '₹6,500', color: 'border-amber-500', featured: true },
              { title: 'Leader',     refs: 20, net: 100, l2: 200, l3: 400,monthly: '₹21,700',color: 'border-violet-500' },
            ].map((e, i) => (
              <div key={i} className={`bg-slate-800 rounded-2xl p-5 border ${e.color} ${e.featured ? 'ring-1 ring-amber-500' : ''}`}>
                {e.featured && <span className="text-[10px] font-black bg-amber-400 text-amber-900 px-2 py-0.5 rounded-full mb-3 inline-block">Most Common</span>}
                <h3 className="text-base font-black text-white mb-4">{e.title}</h3>
                <div className="space-y-2 text-xs text-slate-400 mb-5">
                  <div className="flex justify-between"><span>Direct refs (L1)</span><span className="text-white">{e.refs} people</span></div>
                  <div className="flex justify-between"><span>Total network</span><span className="text-white">{e.net}+ members</span></div>
                  <div className="flex justify-between"><span>Monthly earnings</span><span className="text-amber-400 font-bold text-sm">{e.monthly}</span></div>
                </div>
                <p className="text-[10px] text-slate-500">After ₹500 membership fee</p>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-slate-500">
            Earnings depend on your network activity and renewals. Past performance does not guarantee future results.
          </p>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 bg-white">
        <div className="max-w-xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-black text-slate-900 mb-4" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
            Ready to start?
          </h2>
          <p className="text-sm text-slate-500 mb-8">
            Join SuccessFlow today for ₹500/month. Share your link, grow your network, earn every month.
          </p>
          {user ? (
            <Link to="/plans"
              className="inline-block bg-slate-900 hover:bg-slate-700 text-white font-black text-base px-8 py-4 rounded-2xl no-underline transition-all">
              Choose a Plan →
            </Link>
          ) : (
            <Link to="/login?mode=signup"
              className="inline-block bg-slate-900 hover:bg-slate-700 text-white font-black text-base px-8 py-4 rounded-2xl no-underline transition-all">
              Create Free Account →
            </Link>
          )}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-slate-950 border-t border-slate-800 py-10">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-white rounded-lg flex items-center justify-center">
              <span className="text-slate-900 text-xs font-black">S</span>
            </div>
            <span className="text-white font-black text-sm" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>SuccessFlow</span>
          </div>
          <p className="text-slate-600 text-xs">© {new Date().getFullYear()} SuccessFlow. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/leaderboard" className="text-slate-600 hover:text-slate-300 text-xs no-underline transition-colors">Leaderboard</Link>
            <Link to="/community"   className="text-slate-600 hover:text-slate-300 text-xs no-underline transition-colors">Community</Link>
            <Link to="/login"       className="text-slate-600 hover:text-slate-300 text-xs no-underline transition-colors">Login</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;